import { Button } from "primereact/button";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router";
import PrimeDialog from "../../../components/dialog/PrimeDialog";
import PanelDataTable from "../../../components/PanelDataTable";
import { useEditableFlag } from "../../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../../store/messageStore";
import {
  F106030601Before,
  queryCbamProdManufacturingInfo,
  updateCbamProdManufacturingInfo,
} from "../../../utility/service/cbam-service";
import { getCBAMSubItemsByItemNo } from "../../../utility/service/companyOffering-service";
import { Base64Encoder, BcryptEncoder, checkDpSuccess } from "../../../utility/service/tool-service";
import { addFormValidator } from "../../../utility/validate/formValidator";

const ManufacturingInfo = ({ setActiveIndex }) => {
  const { t } = useTranslation();
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const {
    action,
    setAction,
    currentCompany,
    setCurrentCompany,
    currentCbamMasterSeq,
    setCurrentCbamMasterSeq,
  } = useOutletContext();
  const [formRule, setFormRule] = useState([]); //表單驗證 設備
  // 相關商品類別
  const [prodCateList, setProdCateList] = useState([]);
  const [prodCateMap, setProdCateMap] = useState({});
  const [filiterProdCate, setFiliterProdCate] = useState([]);
  // 產品類別路徑
  const [prodRouteList, setProdRouteList] = useState([]);
  const [prodRouteMap, setProdRouteMap] = useState({});
  const [prodRouteDisabledMap, setProdRouteDisabledMap] = useState({});
  // 生產國家
  const [countryList, setCountryList] = useState([]);

  const isEditable = useEditableFlag("F10603");
  const [tabAction, setTabAction] = useState("detail");
  const editFlag = tabAction === "detail" || !isEditable;
  const [oriDataMap, setOriDataMap] = useState({
    prodCateMap: {},
    prodRouteMap: {},
    prodRouteDisabledMap: {},
    oriData: {},
  });

  const [confirmPopup, setConfirmPopup] = useState(false);
  const [deleteDataFlag, setDelDataFlag] = useState(false);
  const [deleteDataFlagTime, setDelDataFlagTime] = useState(0);
  const [firstLoad, setFirstLoad] = useState(false);

  const {
    getValues,
    setValue,
    watch,
    control,
    register,
    handleSubmit,
    clearErrors,
    trigger,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
  });

  useEffect(() => {
    axios_F106030601Before();
    axios_getCBAMSubItemsByItemNo("CBAM_GOODS_CATEGORY", 2).then(setProdCateList);
    axios_getCBAMSubItemsByItemNo("PRODUCT_ROUTE", 8).then(setProdRouteList);
    axios_getCBAMSubItemsByItemNo("CBAM_COUNTRIES", 0).then(setCountryList);
  }, []);

  useEffect(() => {
    if (prodRouteList.length > 0) {
      axios_queryCbamProdManufacturingInfo({
        companySeq: currentCompany?.companySeq,
        cbamMasterSeq: currentCbamMasterSeq,
      });
    }
  }, [prodRouteList, currentCbamMasterSeq]);

  const handleResetForm = (fieldArrayApi, defaultList, index) => {
    clearErrors();
    fieldArrayApi.remove(index);
    setProdCateMap((prev) => {
      const newMap = { ...prev };
      delete newMap[index];
      return newMap;
    });
    fieldArrayApi.append(defaultList);
  };

  const handleDelForm = (fieldArrayApi, defaultList, index, length) => {
    clearErrors();
    fieldArrayApi.remove(index);
    setProdCateMap((prev) => {
      const newMap = { ...prev };
      delete newMap[index];
      return newMap;
    });
    if (length <= 1) {
      fieldArrayApi.append(defaultList);
    }
  };

  const setValidForm = useCallback(
    (formId) => {
      if (formRule?.length > 0) {
        const tmp = formRule?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [formRule],
  );

  const setValidRouteDataForm = useCallback(
    (formId) => {
      if (setValidForm("cbamRouteDataList")?.length > 0) {
        const tmp = setValidForm("cbamRouteDataList")?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [setValidForm("cbamRouteDataList")],
  );

  const setValidProcessDataForm = useCallback(
    (formId) => {
      if (setValidForm("cbamProcessDataList")?.length > 0) {
        const tmp = setValidForm("cbamProcessDataList")?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [setValidForm("cbamProcessDataList")],
  );

  const setValidPurchasedPrecursorsForm = useCallback(
    (formId) => {
      if (setValidForm("cbamPurchasedPrecursorsList")?.length > 0) {
        const tmp = setValidForm("cbamPurchasedPrecursorsList")?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [setValidForm("cbamPurchasedPrecursorsList")],
  );

  const defaultRouteDataList = {
    cbamRouteDataSeq: "",
    randomCode: crypto.randomUUID(),
  };

  const { fields: cbamRouteDataFields, append: addRouteData, remove: delRouteData } = useFieldArray({
    control,
    name: "cbamRouteDataList",
  });

  const routePanelConfig = {
    header: t("productCategorySummary"),
  };

  const routeDataTableConfig = {
    dataKey: "id",
    value: cbamRouteDataFields,
    item: {
      display: false,
    },
    className: `${editFlag || watch("cbamRouteDataList.length") >= 10 ? "last-row-no-border" : ""}`,
    footerEvent: () => addRouteData(defaultRouteDataList),
    footerDisabled: editFlag || watch("cbamRouteDataList.length") >= 10,
  };

  const routeColumnsConfig = [
    // ID
    {
      field: "dataId",
      fieldName: "dataId",
      fieldListName: "cbamRouteDataList",
      header: "ID",
      frozen: true,
      alignFrozen: "left",
      width: "100px",
      mindth: "100px",
      maxWidth: "100px",
      nonEditor: (value) => true,
      displayText: (rowData, rowIndex) => `G${rowData.dataId ?? rowIndex + 1}`,
    },
    // 管制商品類別
    {
      field: "goodsCategoryCode",
      fieldName: "goodsCategoryCode",
      fieldListName: "cbamRouteDataList",
      header: t("controlledProductCategory"),
      headerRequired: tabAction !== "detail",
      width: "300px",
      type: "dropdown",
      control: control,
      rules: setValidRouteDataForm("goodsCategoryCode"),
      options: prodCateList,
      optionLabel: "subitemName",
      optionValue: "subitemNo",
      placeholder: t("plz_chs"),
      onChange: (rowIndex, newValue) => {
        const filter = prodRouteList.filter((item) => item.param1 === newValue);
        const filterList = filter.map((item) => item.subitemNo);

        setProdCateMap((prev) => ({
          ...prev,
          [rowIndex]: newValue ?? null,
        }));

        setProdRouteMap((prev) => ({
          ...prev,
          [rowIndex]: filter.length > 0 ? filter : [{ subitemNo: "", subitemName: t("allProduct") }],
        }));

        setProdRouteDisabledMap((prev) => ({
          ...prev,
          [rowIndex]: filter.length <= 0,
        }));

        setValue(`cbamRouteDataList.${rowIndex}.route`, filter.length > 0 ? filterList : [""], {
          shouldValidate: true,
          shouldDirty: true,
        });

        // 判斷變更的選項是否已經存在在 生產過程 或 前驅物 中
        const processRouteDataSeqSet = new Set(watch(`cbamProcessDataList`).map((item) => item.cbamRouteDataSeq));
        const precursorsRouteDataSeqSet = new Set(
          watch(`cbamPurchasedPrecursorsList`).map((item) => item.cbamRouteDataSeq),
        );
        const routeSeq =
          watch(`cbamProcessDataList.${rowIndex}.cbamRouteDataSeq`) ||
          watch(`cbamProcessDataList.${rowIndex}.randomCode`);
        const existsInProcess = processRouteDataSeqSet.has(routeSeq);
        const existsInPrecursors = precursorsRouteDataSeqSet.has(routeSeq);
        if (!firstLoad && (existsInProcess || existsInPrecursors)) {
          setDelDataFlag(true);
          if (deleteDataFlagTime < 1) {
            setConfirmPopup(true);
            setDelDataFlagTime(1);
          }
        }
      },
      disabled: editFlag,
    },
    // 相關生產工藝流程
    {
      field: "route",
      fieldName: "route",
      fieldListName: "cbamRouteDataList",
      header: t("productionProcessFlow"),
      type: "multiSelect",
      control: control,
      rules: setValidRouteDataForm("route"),
      customDisabled: prodRouteDisabledMap,
      customOptions: prodRouteMap,
      optionLabel: "subitemName",
      optionValue: "subitemNo",
      placeholder: t("plz_chs"),
      disabled: editFlag,
      onChange: (rowIndex, newValue) => {
        const processRouteDataSeqSet = new Set(watch(`cbamProcessDataList`).map((item) => item.cbamRouteDataSeq));
        const precursorsRouteDataSeqSet = new Set(
          watch(`cbamPurchasedPrecursorsList`).map((item) => item.cbamRouteDataSeq),
        );
        const routeSeq =
          watch(`cbamProcessDataList.${rowIndex}.cbamRouteDataSeq`) ||
          watch(`cbamProcessDataList.${rowIndex}.randomCode`);
        const existsInProcess = processRouteDataSeqSet.has(routeSeq);
        const existsInPrecursors = precursorsRouteDataSeqSet.has(routeSeq);
        if (!firstLoad && (existsInProcess || existsInPrecursors)) {
          setDelDataFlag(true);
          if (deleteDataFlagTime < 1) {
            setConfirmPopup(true);
            setDelDataFlagTime(1);
          }
        }
      },
    },
  ];

  const routeActionConfig = {
    fields: cbamRouteDataFields,
    fieldId: "id",
    handleDelForm: handleDelForm,
    handleResetForm: handleResetForm,
    fieldArrayApi: { remove: delRouteData, append: addRouteData },
    defaultList: defaultRouteDataList,
    disabled: editFlag,
  };

  const { fields: cbamProcessDataFields, update: updateProcessData } = useFieldArray({
    control,
    name: "cbamProcessDataList",
  });

  const equipmentPanelConfig = {
    header: t("relatedProductionProcess"),
  };

  const equipmentDataTableConfig = {
    dataKey: "id",
    item: {
      display: false,
    },
    className: "last-row-no-border",
    value: cbamProcessDataFields,
  };

  const equipmentColumnsConfig = [
    // ID
    {
      field: "dataId",
      fieldName: "dataId",
      fieldListName: "cbamProcessDataList",
      header: "ID",
      frozen: true,
      alignFrozen: "left",
      width: "100px",
      mindth: "100px",
      maxWidth: "100px",
      nonEditor: (value) => true,
      displayText: (rowData, rowIndex) => `P${rowData.dataId ?? rowIndex + 1}`,
    },
    // 名稱
    {
      field: "equipmentName",
      fieldName: "equipmentName",
      fieldListName: "cbamProcessDataList",
      header: t("productName"),
      width: "300px",
      nonEditor: (value) => true,
      displayText: (rowData, rowIndex) => `${rowData.equipmentName}`,
    },
    // 相關產品類別
    {
      field: "cbamRouteDataSeq",
      fieldName: "cbamRouteDataSeq",
      fieldListName: "cbamProcessDataList",
      header: t("relProdCat"),
      headerRequired: tabAction !== "detail",
      type: "dropdown",
      control: control,
      rules: setValidProcessDataForm("cbamRouteDataSeq"),
      options: filiterProdCate,
      optionLabel: "label",
      optionValue: "key",
      placeholder: t("plz_chs"),
      disabled: editFlag,
      onChange: (rowIndex, newValue) => {
        if (!firstLoad) {
          setDelDataFlag(true);
          if (deleteDataFlag < 1) {
            setConfirmPopup(true);
            setDelDataFlagTime(1);
          }
        }
      },
    },
  ];

  const { fields: cbamPurchasedPrecursorsFields, update: updatePurchasedPrecursors } = useFieldArray({
    control,
    name: "cbamPurchasedPrecursorsList",
  });

  const purchasedPrecursorsPanelConfig = {
    header: t("precursor"),
  };

  const purchasedPrecursorsDataTableConfig = {
    dataKey: "id",
    item: {
      display: false,
    },
    className: "last-row-no-border",
    value: cbamPurchasedPrecursorsFields,
  };

  const purchasedPrecursorsColumnsConfig = [
    // ID
    {
      field: "dataId",
      fieldName: "dataId",
      fieldListName: "cbamPurchasedPrecursorsList",
      header: "ID",
      frozen: true,
      alignFrozen: "left",
      width: "100px",
      mindth: "100px",
      maxWidth: "100px",
      nonEditor: (value) => true,
      displayText: (rowData, rowIndex) => `PP${rowData.dataId ?? rowIndex + 1}`,
    },
    // 名稱
    {
      field: "precursorsDataName",
      fieldName: "precursorsDataName",
      fieldListName: "cbamPurchasedPrecursorsList",
      header: t("productName"),
      nonEditor: (value) => true,
      displayText: (rowData, rowIndex) => `${rowData.precursorsDataName}`,
    },
    // 相關產品類別
    {
      field: "cbamRouteDataSeq",
      fieldName: "cbamRouteDataSeq",
      fieldListName: "cbamPurchasedPrecursorsList",
      header: t("relProdCat"),
      headerRequired: tabAction !== "detail",
      type: "dropdown",
      control: control,
      rules: setValidPurchasedPrecursorsForm("cbamRouteDataSeq"),
      options: filiterProdCate,
      optionLabel: "label",
      optionValue: "key",
      placeholder: t("plz_chs"),
      disabled: editFlag,
      onChange: (rowIndex, newValue) => {
        if (!firstLoad) {
          setDelDataFlag(true);
          if (deleteDataFlagTime < 1) {
            setConfirmPopup(true);
            setDelDataFlagTime(1);
          }
        }
      },
    },
    // 生產國家
    {
      field: "countryCode",
      fieldName: "countryCode",
      fieldListName: "cbamPurchasedPrecursorsList",
      header: t("countryOfOrigin"),
      headerRequired: tabAction !== "detail",
      type: "dropdown",
      control: control,
      rules: setValidPurchasedPrecursorsForm("countryCode"),
      options: countryList,
      optionLabel: "subitemName",
      optionValue: "subitemNo",
      placeholder: t("plz_chs"),
      disabled: editFlag,
    },
    // 數量(公噸)
    {
      field: "amountTons",
      fieldName: "amountTons",
      fieldListName: "cbamPurchasedPrecursorsList",
      header: t("quantityTonnes"),
      headerRequired: tabAction !== "detail",
      type: "number",
      control: control,
      rules: setValidPurchasedPrecursorsForm("amountTons"),
      placeholder: t("quantityTonnes"),
      disabled: editFlag,
      value: (rowIndex) => watch(`cbamPurchasedPrecursorsList.${rowIndex}.amountTons`),
      onChange: (rowIndex, newValue) => {
        setValue(`cbamPurchasedPrecursorsList.${rowIndex}.amountTons`, newValue);
      },
    },
  ];

  // 處理相關生產過程和前驅物的產品類別下拉
  const refreshProdCateFilter = () => {
    const currentRoutes = getValues("cbamRouteDataList") || [];

    const newFilter = currentRoutes.map((item, index) => {
      const matchedCate = prodCateList.find((item2) => item2.subitemNo === item.goodsCategoryCode);
      return {
        key: item.cbamRouteDataSeq || item.randomCode,
        label: `G${index + 1} - ${matchedCate?.subitemName ?? ""}`,
      };
    });

    if (JSON.stringify(newFilter) !== JSON.stringify(filiterProdCate)) {
      setFiliterProdCate(newFilter);
    }

    const validKeys = new Set(newFilter.map((f) => f.key));

    cbamProcessDataFields.forEach((item, i) => {
      const currentCode = getValues(`cbamProcessDataList.${i}.cbamRouteDataSeq`);
      if (currentCode && !validKeys.has(currentCode)) {
        updateProcessData(i, { ...item, cbamRouteDataSeq: "" });
      }
    });

    cbamPurchasedPrecursorsFields.forEach((item, i) => {
      const currentCode = getValues(`cbamPurchasedPrecursorsList.${i}.cbamRouteDataSeq`);
      if (currentCode && !validKeys.has(currentCode)) {
        updatePurchasedPrecursors(i, { ...item, cbamRouteDataSeq: "" });
      }
    });
  };

  useEffect(() => {
    refreshProdCateFilter();
  }, [prodCateMap, cbamRouteDataFields, cbamProcessDataFields, cbamPurchasedPrecursorsFields]);

  const processConstraints = (constraints) => {
    return constraints.map((item) => {
      if (item?.subObject?.value) {
        return { [item?.field]: processConstraints(item?.subObject.value) };
      } else {
        return addFormValidator(item?.field, constraints);
      }
    });
  };

  const axios_F106030601Before = () => {
    checkLoading(loading);
    F106030601Before()
      .then((res) => {
        if (checkDpSuccess(res)) {
          setFormRule(processConstraints(res?.data?.RespBody?.constraints));
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  const axios_getCBAMSubItemsByItemNo = (itemNo, orderNo) => {
    return getCBAMSubItemsByItemNo(itemNo, orderNo).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        return data.map((item, index) => ({
          ...item,
          index,
        }));
      }
      return [];
    });
  };

  // 查詢製造資訊
  const axios_queryCbamProdManufacturingInfo = (reqBody, readMore = false) => {
    checkLoading(loading);
    queryCbamProdManufacturingInfo(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          let data = res?.data.RespBody;
          const list = (data?.cbamRouteDataList || []).map((item, index) => {
            const filter = prodRouteList.filter((prodRoute) => prodRoute.param1 === item.goodsCategoryCode);
            const routes = ["route1", "route2", "route3", "route4", "route5", "route6"]
              .map((key) => item[key])
              .filter((val) => val !== null && val !== undefined && val !== "");
            const route = routes.length > 0 ? routes : [""];

            setProdCateMap((prev) => {
              const newMap = { ...prev, [index]: item.goodsCategoryCode ?? null };
              setOriDataMap((prevBackup) => ({ ...prevBackup, prodCateMap: newMap }));
              return newMap;
            });
            setProdRouteMap((prev) => {
              const newMap = {
                ...prev,
                [index]: filter.length > 0 ? filter : [{ subitemNo: "", subitemName: t("allProduct") }],
              };
              setOriDataMap((prevBackup) => ({ ...prevBackup, prodRouteMap: newMap }));
              return newMap;
            });
            setProdRouteDisabledMap((prev) => {
              const newMap = { ...prev, [index]: filter.length <= 0 };
              setOriDataMap((prevBackup) => ({ ...prevBackup, prodRouteDisabledMap: newMap }));
              return newMap;
            });

            return {
              ...item,
              route,
            };
          });

          const oriData = {
            cbamRouteDataList: list,
            cbamProcessDataList: data?.cbamProcessDataList,
            cbamPurchasedPrecursorsList: data?.cbamPurchasedPrecursorsList,
          };
          if (list.length <= 0) {
            setFirstLoad(true);
            list.push(defaultRouteDataList);
          }
          setOriDataMap((prevBackup) => ({ ...prevBackup, oriData: oriData }));
          Object.entries(oriData).forEach(([key, value]) => {
            setValue(key, value);
          });
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  const encoder = (dataList, code) => {
    const findItemIndex = (map, code) => {
      return map.get(code) ?? false;
    };
    if (code === null || code === undefined || code === "") return "";
    if (dataList === null || dataList === undefined || dataList === "" || dataList.length <= 0) return "";
    return Base64Encoder(BcryptEncoder(code ?? "")) + "," + findItemIndex(dataList, code ?? "");
  };

  const onSubmit = async (data) => {
    checkLoading(loading);

    await new Promise((resolve) => setTimeout(resolve, 0));

    const unitMaprodCateMap = new Map(prodCateList.map((v, i) => [v.subitemNo, v.index]));
    const prodRouteMap = new Map(prodRouteList.map((v, i) => [v.subitemNo, v.index]));
    const countryMap = new Map(countryList.map((v, i) => [v.subitemNo, v.index]));

    await Promise.all(
      data.cbamRouteDataList.map(async (item, index) => {
        item.dataId = String(index + 1);
        item.goodsCategoryCode = encoder(unitMaprodCateMap, item.goodsCategoryCode);

        for (let i = 0; i < 6; i++) {
          item[`route${i + 1}`] =
            item.route && item.route[i] !== "" && item.route[i] != null ? encoder(prodRouteMap, item.route[i]) : null;
        }
        delete item["route"];
        delete item["id"];
      }),
    );

    await Promise.all(
      data.cbamProcessDataList.map(async (item, index) => {
        item.dataId = String(index + 1);
        delete item["id"];
      }),
    );

    await Promise.all(
      data.cbamPurchasedPrecursorsList.map(async (item, index) => {
        item.dataId = String(index + 1);
        item.countryCode = encoder(countryMap, item.countryCode);
        delete item["id"];
      }),
    );

    data.cbamMasterSeq = currentCbamMasterSeq;
    delete data["countryCodeName"];
    delete data["goodsCategoryCode"];

    updateCbamProdManufacturingInfo({
      ...data,
      deleteDataFlag: deleteDataFlag ? "Y" : "N",
    })
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
          setActiveIndex(2);
          setConfirmPopup(false);
          setDelDataFlag(false);
          setDelDataFlagTime(0);
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  return (
    <>
      <form className="grid" onSubmit={handleSubmit(onSubmit)}>
        {/* 商品類別匯總 */}
        <div className="col-12">
          <PanelDataTable
            panelConfig={routePanelConfig}
            dataTableConfig={routeDataTableConfig}
            columnsConfig={routeColumnsConfig}
            actionConfig={routeActionConfig}
          />
        </div>
        {/* 設備名稱 */}
        <div className="col-12">
          <PanelDataTable
            panelConfig={equipmentPanelConfig}
            dataTableConfig={equipmentDataTableConfig}
            columnsConfig={equipmentColumnsConfig}
          />
        </div>
        {/* 前驅物 */}
        <div className="col-12">
          <PanelDataTable
            panelConfig={purchasedPrecursorsPanelConfig}
            dataTableConfig={purchasedPrecursorsDataTableConfig}
            columnsConfig={purchasedPrecursorsColumnsConfig}
          />
        </div>

        <div className="col-12 gap-2 flex-all-center">
          {tabAction === "detail" && (
            <Button
              disabled={!isEditable}
              type="button"
              label={t("button.edit")}
              size="small"
              onClick={() => setTabAction("create")}
            />
          )}
          {tabAction === "create" && (
            <>
              <Button
                disabled={
                  !(isValid && isDirty) ||
                  (getValues("cbamProcessDataList")?.length ?? 0) <= 0 ||
                  (getValues("cbamPurchasedPrecursorsList")?.length ?? 0) <= 0
                }
                type="submit"
                label={action === "create" ? t("button.create") : t("button.update")}
                size="small"
              />
              {action !== "create" && (
                <Button
                  outlined
                  size="small"
                  label={t("button.cancel")}
                  onClick={(e) => {
                    e.preventDefault();
                    clearErrors();
                    setTabAction("detail");
                    reset(oriDataMap?.oriData);
                    setProdCateMap(oriDataMap?.prodCateMap);
                    setProdRouteMap(oriDataMap?.prodRouteMap);
                    setProdRouteDisabledMap(oriDataMap?.prodRouteDisabledMap);
                    setDelDataFlag(false);
                    setDelDataFlagTime(0);
                  }}
                />
              )}
            </>
          )}
        </div>
      </form>

      <PrimeDialog
        visible={confirmPopup}
        isShowCancelBtn={false}
        onConfirm={() => {
          setConfirmPopup(false);
        }}
        onClose={() => {
          setConfirmPopup(false);
        }}
        title={t("hint")}
        size="sm"
        bodyTemplate={t("manufacturingInfoClearTip", {
          item: [t("process"), t("precursor"), t("productOverview")].join(", "),
        })}
      />
    </>
  );
};

export default ManufacturingInfo;
